import { Loader } from '@10xLabs/react-web/components/Loader'
import { NetworkStatus, useQuery } from '@apollo/client'
import { isEmpty } from 'lodash'
import { ReactElement, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom'

import { PaginationList } from 'components/PaginationList'
import { Button } from 'components/v2/Button'
import { Content, ContentBody, ContentHeader } from 'components/v2/Content'
import EmptyState from 'components/v2/EmptyState'
import Heading from 'components/v2/Heading'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { useBusinessClientAutocomplete } from 'features/BusinessClients/hooks'
import { useLogin } from 'features/Login/hooks'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { canRead } from 'features/Users/utils'
import { GET_BUSINESS_CLIENT_USERS } from 'features/BusinessClientUsers/queries'
import { IBusinessClientUsersFilters } from 'features/BusinessClientUsers/types'
import { filterEmptyValue } from 'utils/object'
import { getQueries, getQueryString } from 'utils/route'
import { BusinessClientUsersList } from 'features/BusinessClientUsers/components/BusinessClientUsersList/List'
import { BusinessClientUsersFilters } from 'features/BusinessClientUsers/views/Index/Filters'

const BUSINESS_CLIENTS_USERS_LIMIT = 30

const messages = defineMessages({
  helmet: {
    id: 'businessClientUsers.helmet',
    defaultMessage: 'Business Client Users | Nexbus Admin'
  }
})

const BusinessClientUsersView = (): ReactElement => {
  let content: ReactElement
  const intl = useIntl()
  const login = useLogin()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const pageNb = parseInt(searchParams.get('page') ?? '1')
  const searchQueries = getQueries(location.search)
  const businessClientAutocomplete = useBusinessClientAutocomplete(searchQueries.businessClientId, {
    skip: !searchQueries.businessClientId
  })
  const filters: IBusinessClientUsersFilters = {
    businessClient: {
      id: businessClientAutocomplete?.id || '',
      name: businessClientAutocomplete?.name || ''
    }
  }
  const canReadBusinessClientUser = canRead(FEATURES.BUSINESS_CLIENT_USER, login.accessToken.policies)
  const queryInput = filterEmptyValue({
    businessClientID: searchQueries.businessClientId
  })

  const { loading, error, data, refetch, networkStatus } = useQuery(GET_BUSINESS_CLIENT_USERS, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !canReadBusinessClientUser,
    variables: {
      input: queryInput,
      pagination: {
        page: pageNb,
        size: BUSINESS_CLIENTS_USERS_LIMIT
      }
    }
  })

  useEffect((): void => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  if (!canReadBusinessClientUser) {
    return <ForbiddenView feature={FEATURES.BUSINESS_CLIENT_USER} />
  }

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="businessClientUsers.error.description"
            defaultMessage="There was an error while loading the users"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button
            onClick={(): void => {
              refetch()
            }}
            kind="primary"
          >
            <FormattedMessage id="businessClientUsers.error.action" defaultMessage="Reload the users" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data?.businessClientUsers.items) && pageNb === 1 && isEmpty(queryInput)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage id="businessClientUsers.empty.description" defaultMessage="You don't have any users yet" />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/business-clients-users/new" kind="primary">
            <FormattedMessage id="businessClientUsers.empty.action" defaultMessage="Add a user first" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data?.businessClientUsers.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="businessClientUsers.empty.filters.description"
            defaultMessage="There was no users found according to your filters"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/business-client-users" kind="primary">
            <FormattedMessage id="businessClientUsers.empty.filters.action" defaultMessage="Reset filters" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <BusinessClientUsersList businessClientUsers={data?.businessClientUsers.items ?? []} showBusinessClient />
        <PaginationList
          page={pageNb}
          totalItems={data?.businessClientUsers.total ?? 0}
          limit={BUSINESS_CLIENTS_USERS_LIMIT}
          currentItemsCount={data?.businessClientUsers.items.length ?? 0}
        />
      </>
    )
  }

  const handleFiltersSubmit = (values: IBusinessClientUsersFilters): void => {
    const queryString = getQueryString({
      page: pageNb,
      businessClientId: values.businessClient?.id
    })
    navigate(`/business-client-users?${queryString}`)
  }

  return (
    <Content>
      <ContentHeader>
        <Heading.H3>
          <FormattedMessage id="businessClientUsers.header.label" defaultMessage="Business Client Users" />
        </Heading.H3>
      </ContentHeader>
      <ContentBody>
        <BusinessClientUsersFilters filters={filters} onSubmit={handleFiltersSubmit} />
        <section>{content}</section>
      </ContentBody>
    </Content>
  )
}

export default withLastUrl(BusinessClientUsersView)
